import { useState } from 'react';
import { firebaseAuth } from '../../lib/firebase';
import { ApiError, apiErrorFromResponse } from '../../lib/api';
import { writeGridFiltersToParams, type GridFilters } from '../../lib/gridFilterUrl';
import type { ExportScope } from './GoogleAdsExportModal';

// Hook that triggers the two Google CSV downloads
// (docs/GoogleCsvExport_Spec.md §3 + §5). Returns a busy flag and
// two trigger functions. The hook handles:
//
//   1. Auth token attachment — Firebase ID token in the Authorization
//      header. Plain <a href> won't work for authenticated GET because
//      browsers don't add custom headers to anchor navigations, so we
//      do fetch → blob → object URL → anchor click → revoke.
//   2. Filter querystring serialisation via the existing gridFilterUrl
//      codec, so the server's readGridFiltersFromParams sees the same
//      shape the grid wrote.
//   3. Filename extraction from Content-Disposition (the server-built
//      "{monthYearSlug}-google-{ads|budget}-{timestamp}.csv"), with a
//      generic fallback if the header is missing for any reason.
//   4. Error surfacing as ApiError so callers can render the same
//      error UX they use elsewhere in the rollouts module.

type AdsExportArgs = {
  rolloutId: string;
  scope: ExportScope;
  filters: GridFilters;
  campaignNameTemplate: string;
  adGroup: string;
};

type BudgetExportArgs = {
  rolloutId: string;
  scope: ExportScope;
  filters: GridFilters;
  campaignNameTemplate: string;
};

export function useGoogleExport(): {
  busy: boolean;
  exportAdsCsv: (args: AdsExportArgs) => Promise<void>;
  exportBudgetCsv: (args: BudgetExportArgs) => Promise<void>;
} {
  const [busy, setBusy] = useState(false);

  async function downloadCsv(path: string, fallbackName: string): Promise<void> {
    const current = firebaseAuth.currentUser;
    if (!current) throw new ApiError(401, 'Not signed in');
    const token = await current.getIdToken();
    const res = await fetch(path, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      // Server returned a JSON error envelope on /api/* — keep the
      // ApiError shape consistent with the rest of the module.
      // apiErrorFromResponse also maps a Cloudflare 524 to StillRunningError.
      throw await apiErrorFromResponse(res);
    }
    const filename = parseFilenameFromContentDisposition(
      res.headers.get('Content-Disposition'),
    ) ?? fallbackName;
    const blob = await res.blob();
    triggerBlobDownload(blob, filename);
  }

  async function exportAdsCsv(args: AdsExportArgs): Promise<void> {
    setBusy(true);
    try {
      const url = buildExportUrl(`/api/rollouts/${args.rolloutId}/google-ads-export.csv`, {
        scope: args.scope,
        filters: args.filters,
        campaignNameTemplate: args.campaignNameTemplate,
        adGroup: args.adGroup,
      });
      await downloadCsv(url, 'google-ads.csv');
    } finally {
      setBusy(false);
    }
  }

  async function exportBudgetCsv(args: BudgetExportArgs): Promise<void> {
    setBusy(true);
    try {
      const url = buildExportUrl(`/api/rollouts/${args.rolloutId}/google-budget-export.csv`, {
        scope: args.scope,
        filters: args.filters,
        campaignNameTemplate: args.campaignNameTemplate,
      });
      await downloadCsv(url, 'google-budget.csv');
    } finally {
      setBusy(false);
    }
  }

  return { busy, exportAdsCsv, exportBudgetCsv };
}

// Exported for unit testing. `campaignNameTemplate` is required by both
// endpoints (server schema enforces min(1)); `adGroup` is Ads-only.
export function buildExportUrl(
  basePath: string,
  args: {
    scope: ExportScope;
    filters: GridFilters;
    campaignNameTemplate: string;
    adGroup?: string;
  },
): string {
  const params = new URLSearchParams();
  params.set('scope', args.scope);
  if (args.scope === 'filtered') {
    // Encode the filter state through the same codec the URL uses.
    const filterParams = new URLSearchParams();
    writeGridFiltersToParams(filterParams, args.filters);
    const encoded = filterParams.toString();
    if (encoded.length > 0) params.set('filters', encoded);
  }
  params.set('campaignNameTemplate', args.campaignNameTemplate);
  if (args.adGroup !== undefined) params.set('adGroup', args.adGroup);
  return `${basePath}?${params.toString()}`;
}

// Content-Disposition header → filename. Handles both:
//   attachment; filename="apr-26-google-ads-202604281614.csv"
//   attachment; filename=apr-26-google-ads-202604281614.csv
// Returns null when no filename token is present (caller falls back).
// Exported for unit testing.
export function parseFilenameFromContentDisposition(
  header: string | null,
): string | null {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match?.[1] ?? null;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick so the click handler has consumed the URL.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
